import { api, apiErrorFromResponse, signalSessionLost, tryRefreshToken } from '~/lib/api-client';

/**
 * Publish stream (FRONT-3021): the `POST /api/pro-builder/projects/:name/publish`
 * response is `text/event-stream` with frames `event: <name>\ndata: <json>\n\n`
 * and `: ping` comment heartbeats. Pure parsing lives here so it is testable
 * under plain node; `streamPublish` is the thin fetch around it.
 */
export type PublishPhase = 'installing' | 'building' | 'bundling' | 'reading' | 'publishing';
export type PublishedAppStatus = 'draft' | 'live' | 'archived';

export interface PublishedApp {
  slug: string;
  title: string;
  url: string;
  status: PublishedAppStatus;
  updatedAt: string;
}

export interface GitPrepared {
  operationId: string;
  name: string;
  app: { slug: string; title: string };
  owner: boolean;
  tip: string;
  main: string;
  live: string | null;
  files: string[];
  audience: string;
  warnings: string[];
}

export interface GitSaved {
  operationId: string;
  commit: string;
  message: string;
  localChanges: boolean;
  /** Links, special files and .env templates this save left out of history. */
  notSaved?: string[];
}

/** What a checkout review's local files are, judged by content (src/pro-builder/git-lifecycle.ts `GitLocalVersion`); only a checkout review carries it. */
export type GitLocalVersion =
  | { kind: 'older-saved'; commit: string; savedAt: string }
  | { kind: 'unsaved'; checked: number; complete: boolean }
  | { kind: 'unknown' };

export interface GitNeedsAction {
  code: string;
  message: string;
  paths: string[];
  operationId?: string;
  actions?: Array<'retry' | 'update' | 'reconcile'>;
  confirmation?: string;
  versions?: Array<{ path: string; local: string | null; current: string | null; binary?: boolean; truncated?: boolean }>;
  localVersion?: GitLocalVersion;
}

export type PublishEvent =
  | ({ type: 'prepared' } & GitPrepared)
  | ({ type: 'saved' } & GitSaved)
  | ({ type: 'needs-action' } & GitNeedsAction)
  | { type: 'progress'; phase: PublishPhase; message: string }
  | { type: 'done'; app: PublishedApp; created: boolean; warnings: string[] }
  | { type: 'error'; code: string; message: string; detail?: string };

const isStringArray = (value: unknown): boolean => Array.isArray(value) && value.every((item) => typeof item === 'string');

/** A `prepared` frame carries the review's identity, commits, audience and file list. */
function isPreparedBody(body: Record<string, unknown>): boolean {
  const app = body.app as Record<string, unknown> | null | undefined;
  const namedApp = !!app && typeof app === 'object' && typeof app.slug === 'string' && typeof app.title === 'string';
  const liveCommit = body.live === null || typeof body.live === 'string';
  const commits = typeof body.tip === 'string' && typeof body.main === 'string' && liveCommit;
  return typeof body.operationId === 'string' && typeof body.name === 'string' && namedApp && typeof body.owner === 'boolean'
    && commits && typeof body.audience === 'string' && isStringArray(body.files) && isStringArray(body.warnings);
}

/** Turn one complete frame's text into an event; null for comments, unknown events and bad JSON. */
function frameToEvent(frame: string): PublishEvent | null {
  let name = '';
  const data: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) name = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
  }
  if (!data.length) return null;
  let body: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(data.join('\n'));
    if (!parsed || typeof parsed !== 'object') return null;
    body = parsed as Record<string, unknown>;
  } catch {
    return null;
  }
  if (name === 'prepared' && isPreparedBody(body)) {
    return { ...body, type: 'prepared' } as PublishEvent;
  }
  if (name === 'saved' && typeof body.operationId === 'string' && typeof body.commit === 'string'
    && typeof body.message === 'string' && typeof body.localChanges === 'boolean') {
    return { ...body, type: 'saved' } as PublishEvent;
  }
  if (name === 'needs-action' && typeof body.code === 'string' && typeof body.message === 'string' && Array.isArray(body.paths)) {
    return { ...body, type: 'needs-action' } as PublishEvent;
  }
  if (name === 'progress' && typeof body.message === 'string') {
    return { type: 'progress', phase: body.phase as PublishPhase, message: body.message };
  }
  if (name === 'done' && body.app && typeof body.app === 'object') {
    return {
      type: 'done',
      app: body.app as PublishedApp,
      created: body.created === true,
      warnings: Array.isArray(body.warnings) ? (body.warnings as string[]) : [],
    };
  }
  if (name === 'error' && typeof body.message === 'string') {
    return {
      type: 'error',
      code: typeof body.code === 'string' ? body.code : 'unknown',
      message: body.message,
      ...(typeof body.detail === 'string' && body.detail ? { detail: body.detail } : {}),
    };
  }
  return null;
}

/**
 * Incremental parser: feed chunks as they arrive (a frame may be split across
 * chunks, or several may share one) and get back the events completed so far.
 */
export function createPublishStreamParser() {
  let buffer = '';
  return {
    push(chunk: string): PublishEvent[] {
      buffer += chunk.replace(/\r\n/g, '\n');
      const events: PublishEvent[] = [];
      let at: number;
      while ((at = buffer.indexOf('\n\n')) !== -1) {
        const ev = frameToEvent(buffer.slice(0, at));
        buffer = buffer.slice(at + 2);
        if (ev) events.push(ev);
      }
      return events;
    },
  };
}

/**
 * POST the publish (one silent token refresh and retry on a 401) and relay each event. A response that is not an event
 * stream is a JSON refusal and is thrown as the shared `ApiError`, whose
 * message is the server's plain sentence. Resolves when the stream ends.
 */
export async function streamPublish(name: string, onEvent: (e: PublishEvent) => void): Promise<void> {
  const path = `/api/pro-builder/projects/${encodeURIComponent(name)}/publish`;
  const post = () => api.raw(path, { method: 'POST', credentials: 'same-origin' });
  let res = await post();
  // `api.raw` has no silent refresh, so a publish after the hourly access-token
  // lapse would 401. Do what `request()` does: one refresh, one retry.
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed === 'ok') res = await post();
    else if (refreshed === 'expired') signalSessionLost();
  }
  const type = res.headers.get('content-type') ?? '';
  if (!res.ok || !type.includes('text/event-stream') || !res.body) throw await apiErrorFromResponse(res, path);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const parser = createPublishStreamParser();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    for (const ev of parser.push(decoder.decode(value, { stream: true }))) onEvent(ev);
  }
}
